import { GroupDots, PageDots } from "../version/Collab";
import { SearchIcon } from "./SearchPalette";
import { useEffect, useRef, useState } from "react";
import { isSingle, itemVisible, navWithTiles, type NavGroup, type NavItem } from "../../lib/nav";
import { useT, type MsgKey } from "../../lib/i18n";
import type { Tile } from "../../lib/tiles";

function Dot({ title }: { title: string }) {
  return <span title={title} className="ml-auto h-1.5 w-1.5 flex-none rounded-full bg-line" />;
}

/** 展开/收起箭头：收起朝右、展开朝下 */
function Chevron({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 12 12" aria-hidden className={`h-3 w-3 flex-none text-ink-placeholder transition-transform ${on ? "rotate-90" : ""}`}>
      <path d="M4.5 2.5 8 6l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * 层级靠三样拉开：一级（组标题）正文色加粗 14px；二级 13px 常规色，挂在一条竖向引导线上；
 * 三级再缩进一条线、颜色再浅一档。选中项把引导线那一段描成主色。
 * 只靠缩进和字重差一点点，运营看不出哪个是组、哪个是页（leo 2026-10-16）。
 */
function itemCls(selected: boolean, indent: 0 | 1 | 2) {
  // 用 min-h + 竖向内边距而不是固定高度：英文标签更长（Combat & Dice Control
  // 要换行），固定行高会让第二行溢出、看起来像坏了。
  if (indent === 0) {
    return (
      "flex min-h-[40px] cursor-pointer items-center gap-1.5 px-4 py-1 text-base font-semibold leading-snug " +
      (selected ? "bg-primary-50 text-primary" : "text-ink hover:bg-fill-light")
    );
  }
  return (
    // -ml-px 让左边框压在父容器的引导线上：未选中透明（露出引导线），选中描主色
    `-ml-px flex cursor-pointer items-center gap-1.5 border-l-2 py-1 pl-3 pr-3 text-sm leading-snug ` +
    (indent === 2 ? "min-h-[28px] " : "min-h-[30px] ") +
    (selected
      ? "border-primary bg-primary-50 font-medium text-primary"
      : `border-transparent hover:bg-fill-light hover:text-ink ${indent === 2 ? "text-ink-secondary" : "text-ink-regular"}`)
  );
}

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** 组里所有页（含三级），用来判断当前页在不在这组、收起时汇总协同圆点 */
const pagesOf = (g: NavGroup) => g.items.flatMap((it) => [it.id, ...(it.children ?? []).map((c) => c.id)]);

export function SideNav({ page, onPage, onSearch, tiles, selectors, isSuperAdmin, isRoot }: {
  page: string;
  onPage: (id: string) => void;
  /** 打开搜索（⌘K 同一个面板） */
  onSearch?: () => void;
  /** 本版本的玩法格：种子之外的也要有入口 */
  tiles?: Tile[];
  /** 当前版本的两个选择器：菜单按它们只显示这个代理真的用得上的那几组 */
  selectors?: { buildingSystem?: string; gameMode?: string };
  /** 超管标记由服务端给（/debug-trace 的 is_super_admin），不由前端猜 */
  isSuperAdmin?: boolean;
  /** 根超管标记（/debug-trace 的 is_root）。rootOnly 菜单项据它显示 */
  isRoot?: boolean;
}) {
  const t = useT();
  const nav = navWithTiles(tiles ?? [], selectors ?? {});
  /*
   * 分组手风琴：默认只展开当前页所在那组，点开别的组时收起上一个手动展开的组。
   *
   * 以前全部展开：单人 + 关卡制的代理有十几组、五十多项，菜单高 2500px，
   * 要滚三屏才找得到「工具」，当前页在哪一组也看不出来。
   * 当前页所在组、当前三级页的父项**一律展开**，不看 group——用 URL 直接进来、
   * 或从别处跳页时没有点击事件，靠点击状态会让当前页藏在收起的组里。
   */
  const [group, setGroup] = useState<string | null>(null);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const toggleGroup = (id: string, current: boolean) => setGroup((g) => (g === id || current ? null : id));
  const toggleItem = (id: string) => setOpenItems((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  // 换页后把选中项滚进视野：组展开后当前项可能落在菜单可视区外面
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    setGroup(null);
    navRef.current?.querySelector("[aria-current=page]")?.scrollIntoView({ block: "nearest" });
  }, [page]);
  // 字典没有这一项（服务端新加的玩法格）时退回菜单项自带的名字，不让裸 key 上菜单
  const label = (it: NavItem) => { const k = `nav.${it.id}`; const s = t(k as MsgKey); return s === k ? it.label : s; };
  const visible = (it: NavItem) => itemVisible(it, { isSuperAdmin, isRoot });

  const row = (it: NavItem, indent: 0 | 1 | 2) => {
    const kids = it.children ?? [];
    // 有子项的父级自己也是一页（总览）：点它同时进总览并展开，
    // 而不是只当开合开关——否则「副玩法配置」这个总览页永远点不进去。
    const expanded = openItems.includes(it.id) || it.id === page || kids.some((c) => c.id === page);
    return (
      <div key={it.id}>
        <div
          onClick={() => { if (kids.length) toggleItem(it.id); onPage(it.id); }}
          aria-current={page === it.id ? "page" : undefined}
          className={itemCls(page === it.id, indent)}
        >
          <span className={kids.length ? "flex-1" : undefined}>{label(it)}</span>
          {!it.done && <Dot title={t("nav.notStarted")} />}
          <PageDots page={it.id} />
          {kids.length > 0 && <Chevron on={expanded} />}
        </div>
        {expanded && kids.length > 0 && <div className="ml-4 border-l border-line-light">{kids.map((c) => (
          <div key={c.id} onClick={(e) => { e.stopPropagation(); onPage(c.id); }} aria-current={page === c.id ? "page" : undefined} className={itemCls(page === c.id, 2)}>
            <span>{label(c)}</span>
            {!c.done && <Dot title={t("nav.notStarted")} />}
            <PageDots page={c.id} />
          </div>
        ))}</div>}
      </div>
    );
  };
  return (
    <nav
      ref={navRef}
      /* 220px 而不是 194：英文菜单名更长，窄了会挤成两行 */
      className="min-h-full w-[220px] flex-none border-r border-line-light bg-white pb-1.5"
    >
      {onSearch && (
        // 钉在菜单顶上：菜单滚到底也随手能搜
        <div className="sticky top-0 z-10 bg-white px-3 pb-1.5 pt-2.5">
          <button
            type="button"
            onClick={onSearch}
            className="flex h-8 w-full items-center gap-2 rounded-base border border-line bg-fill-lighter px-2.5 text-sm text-ink-placeholder hover:border-primary hover:text-ink-secondary"
          >
            <SearchIcon className="h-3.5 w-3.5 flex-none" />
            <span className="flex-1 truncate text-left">{t("search.trigger")}</span>
            <kbd className="flex-none rounded-sm border border-line bg-white px-1 text-xs leading-4">{IS_MAC ? "⌘K" : "Ctrl K"}</kbd>
          </button>
        </div>
      )}
      {nav.map((g) => {
        if (isSingle(g)) return row(g.items[0], 0);
        const pages = pagesOf(g);
        const current = pages.includes(page);
        const on = group === g.id || current;
        return (
          <div key={g.id} className="border-b border-line-lighter last:border-b-0">
            <div
              onClick={() => toggleGroup(g.id, current)}
              className={
                "flex h-[40px] cursor-pointer select-none items-center gap-1.5 px-4 text-base font-semibold text-ink hover:bg-fill-light"
              }
            >
              <span className="flex-1">{t(`navGroup.${g.id}` as MsgKey)}</span>
              {!on && <GroupDots pages={pages} />}
              <Chevron on={on} />
            </div>
            {on && <div className="mb-2 ml-[22px] border-l border-line-light">{g.items.filter(visible).map((it) => row(it, 1))}</div>}
          </div>
        );
      })}
    </nav>
  );
}
